<template>
  <div ref="rootRef" class="max-w-4xl" data-cy="webhook-event-catalog-tab">
    <p class="mb-4 text-sm text-gray-600">
      These are the events an endpoint can subscribe to. Each delivery is an
      HTTPS POST with a JSON body like the examples below, signed so your
      endpoint can check it came from Sera. Before you go live, read
      <a
        href="#verifying-webhooks"
        class="link"
        @click.prevent.stop="scrollToVerifying()"
        >Verifying webhooks</a
      >.
    </p>
    <p class="mb-4 text-sm text-gray-600" data-cy="webhook-catalog-ids-only">
      Payloads are generally limited to ids and a few facts about the event, not
      the record's details.
      <template v-if="apiAccess">
        To read a record itself, create a key on the
        <a
          :href="API_ACCESS_PATH"
          class="link"
          data-cy="webhook-api-access-link"
          >API access</a
        >
        page and read it back from Sera's API by the same id.
      </template>
    </p>

    <section
      v-for="group in groups"
      :key="group.group"
      class="mb-8"
      :data-cy="`webhook-catalog-group:${group.group}`"
    >
      <h2 class="mb-3 text-lg font-semibold text-gray-900">
        {{ group.group }}
      </h2>
      <article
        v-for="eventType in group.eventTypes"
        :id="`event-${eventType.event_type}`"
        :key="eventType.event_type"
        class="mb-4 rounded-lg border border-gray-200 bg-white p-4"
        :data-cy="`webhook-catalog-event:${eventType.event_type}`"
      >
        <div class="mb-1 flex flex-wrap items-baseline gap-2">
          <h3 class="m-0 text-base font-semibold text-gray-900">
            {{ eventType.name }}
          </h3>
          <code class="text-sm text-gray-700">{{ eventType.event_type }}</code>
          <span class="text-xs text-gray-500"
            >version {{ eventType.version }}</span
          >
        </div>
        <p
          class="m-0 mb-2 text-sm text-gray-700"
          data-cy="webhook-catalog-description"
        >
          {{ eventType.description }}
        </p>
        <p
          v-if="eventType.when_it_fires"
          class="m-0 mb-3 text-sm text-gray-700"
          data-cy="webhook-catalog-when"
        >
          <span class="font-semibold text-gray-900">When it fires:</span>
          {{ eventType.when_it_fires }}
        </p>

        <p
          v-if="apiAccess && eventType.reads?.length"
          class="m-0 mb-3 text-sm text-gray-700"
          data-cy="webhook-catalog-reads"
        >
          <span class="font-semibold text-gray-900">Read with the API: </span>
          <template
            v-for="(read, index) in eventType.reads"
            :key="read.resource"
            ><template v-if="index">, </template>
            <a
              :href="docsHref(read.page, read.section)"
              class="link"
              :data-cy="`webhook-catalog-read:${read.resource}`"
              >{{ read.label }}</a
            >
            (<code>{{ read.fields.join(", ") }}</code
            >)</template
          >
        </p>

        <div class="mb-3 overflow-x-auto">
          <table
            class="w-full text-left text-sm"
            data-cy="webhook-catalog-fields"
          >
            <thead class="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th class="px-2 py-1 font-semibold">Field in data</th>
                <th class="px-2 py-1 font-semibold">Type</th>
                <th class="px-2 py-1 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="field in eventType.data_fields"
                :key="field.name"
                class="border-b border-gray-100 last:border-b-0"
              >
                <td class="whitespace-nowrap px-2 py-1 font-mono text-xs">
                  {{ field.name }}
                </td>
                <td
                  class="whitespace-nowrap px-2 py-1 font-mono text-xs text-gray-600"
                >
                  {{ field.type }}
                </td>
                <td class="px-2 py-1 text-gray-700">{{ field.description }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <JsonViewer :value="eventType.sample" title="Example payload" />
      </article>
    </section>

    <section
      id="verifying-webhooks"
      ref="verifyingRef"
      class="scroll-mt-4 space-y-4 text-sm text-gray-700"
      data-cy="webhook-verifying-docs"
    >
      <h2 class="m-0 text-lg font-semibold text-gray-900">
        Verifying webhooks
      </h2>
      <Alert type="warning" data-cy="webhook-docs-warning">
        <strong>{{ SIGNATURE_WARNING }}</strong>
        Sera signs every request, but the signature only protects you if your
        endpoint checks it. Sera can't tell whether it does.
      </Alert>

      <div>
        <h3 class="mb-1 text-base font-semibold text-gray-900">Headers</h3>
        <p class="m-0 mb-2">
          Sera follows
          <a
            href="https://www.standardwebhooks.com/"
            target="_blank"
            rel="noopener"
            class="link"
            >Standard Webhooks</a
          >. Every request carries:
        </p>
        <dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          <dt><code>webhook-id</code></dt>
          <dd class="m-0">
            The delivery's id (<code>whmsg_…</code>). The same on every retry of
            that delivery, including a manual retry.
          </dd>
          <dt><code>webhook-timestamp</code></dt>
          <dd class="m-0">When this attempt was signed, in Unix seconds.</dd>
          <dt><code>webhook-signature</code></dt>
          <dd class="m-0">
            <code>v1,&lt;signature&gt;</code>. During a secret rotation, two
            signatures separated by a space.
          </dd>
        </dl>
      </div>

      <div>
        <h3 class="mb-1 text-base font-semibold text-gray-900">
          Use a Standard Webhooks library
        </h3>
        <p class="m-0 mb-2">
          The official libraries check the signature with your endpoint's
          signing secret and reject a timestamp more than 5 minutes old. Pass
          the raw request body, exactly as received: parsing and re-serializing
          the JSON changes it and breaks the signature. If verification fails,
          respond with 400 or 401 and don't process the request.
        </p>
        <VerificationSnippet />
      </div>

      <div data-cy="webhook-docs-manual">
        <h3 class="mb-1 text-base font-semibold text-gray-900">
          Verifying without a library
        </h3>
        <ol class="m-0 list-decimal space-y-1 pl-5">
          <li>
            Build the signed content:
            <code>{webhook-id}.{webhook-timestamp}.{body}</code>, using the raw
            body.
          </li>
          <li>
            Take the secret without its <code>whsec_</code> prefix and
            base64-decode it. That's the key.
          </li>
          <li>
            Compute the HMAC-SHA256 of the signed content with the key, and
            base64-encode it.
          </li>
          <li>
            Accept the request if your signature matches any
            <code>v1,</code> entry in <code>webhook-signature</code>. Compare
            with a constant-time function, never <code>==</code>.
          </li>
          <li>
            Reject the request if <code>webhook-timestamp</code> is more than 5
            minutes before or after your server's clock. This stops someone
            replaying a request they captured.
          </li>
        </ol>
      </div>

      <div data-cy="webhook-docs-duplicates">
        <h3 class="mb-1 text-base font-semibold text-gray-900">
          Skip duplicates
        </h3>
        <p class="m-0">
          The same event can arrive more than once, for example when your server
          processed a request but its response didn't reach Sera. The
          <code>webhook-id</code> header is the same on every retry of a
          delivery, so record the ids you've processed and skip any you've seen.
          Events can also arrive out of order; use <code>occurred_at</code> to
          order them.
        </p>
      </div>

      <div data-cy="webhook-docs-rotation">
        <h3 class="mb-1 text-base font-semibold text-gray-900">
          Rotating the secret
        </h3>
        <p class="m-0">
          {{ ROTATION_EXPLANATION }} During those 24 hours both signatures are
          sent in <code>webhook-signature</code>, separated by a space. Rotating
          again during the overlap stops the oldest secret from signing right
          away.
        </p>
      </div>

      <div>
        <h3 class="mb-1 text-base font-semibold text-gray-900">Responding</h3>
        <p class="m-0">
          Respond with any 2xx status within 10 seconds, and do the work
          afterwards. Sera doesn't follow redirects. Anything else is retried up
          to 8 times over about 27 hours. A 410 Gone tells Sera to stop and
          disables the endpoint.
        </p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import JsonViewer from "@/components/JsonViewer.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import type { WebhookEventType } from "@/vuex/modules/integrationWebhooks";
import { API_ACCESS_PATH, docsHref } from "../ApiAccess/apiAccess";
import VerificationSnippet from "./VerificationSnippet.vue";
import {
  groupEventTypes,
  ROTATION_EXPLANATION,
  SIGNATURE_WARNING
} from "./webhooksHub";

// The in-app copy of the public docs. Their Markdown source is
// docs/webhooks/*.md; change both together.

const props = withDefaults(
  defineProps<{
    eventTypes: WebhookEventType[];
    // "verifying" when opened from an endpoint drawer's docs link.
    focusSection?: string | null;
  }>(),
  { focusSection: null }
);

const verifyingRef = ref<HTMLElement | null>(null);
const rootRef = ref<HTMLElement | null>(null);
const apiAccess = useFeatureFlag("api_access");

const groups = computed(() => groupEventTypes(props.eventTypes));

function scrollToVerifying(behavior: ScrollBehavior = "smooth") {
  verifyingRef.value?.scrollIntoView?.({ behavior, block: "start" });
}

// The API access Docs tab links an event here with ?section=<event type>.
function scrollToEvent(eventType: string) {
  const articles = rootRef.value?.querySelectorAll<HTMLElement>("article[id]");
  Array.from(articles ?? [])
    .find((article) => article.id === `event-${eventType}`)
    ?.scrollIntoView?.({ behavior: "auto", block: "start" });
}

// The endpoint drawer links here with ?section=verifying, not a #hash: the
// SPA's link interceptor routes by path and query and drops the hash. The
// public anchor (#verifying-webhooks) still works on a full page load.
onMounted(async () => {
  if (
    props.focusSection === "verifying" ||
    window.location.hash === "#verifying-webhooks"
  ) {
    await nextTick();
    scrollToVerifying("auto");
  } else if (
    props.focusSection &&
    props.eventTypes.some((type) => type.event_type === props.focusSection)
  ) {
    await nextTick();
    scrollToEvent(props.focusSection);
  }
});
</script>
